import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  IconButton,
  OutlinedInput,
  Slider,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useMemo } from "react";

import {
  getSegmentLanguageOptions,
  getVoiceStyleOptions,
  isMultilingualVoiceLlm,
} from "Forms/Utils/EnumOptions";
import {
  ActorStatus,
  ActorVoiceStatus,
  type LanguageCodes,
  VoiceLlm,
  type VoiceSpan,
  type VoiceStyle,
} from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useMediaFilesQuery } from "MusicAudio/Api/Queries/useMediaFilesQuery";
import { useProjectActorVoicesQuery } from "Projects/Api/Queries/useProjectActorVoicesQuery";
import type {
  MediaSegmentFE,
  PauseSegmentFE,
  VoiceSegmentFE,
} from "Projects/Components/ScriptEditor/ScriptEditorContext";
import {
  isMediaSegment,
  isPauseSegment,
  isVoiceSegment,
  useScriptEditor,
} from "Projects/Components/ScriptEditor/ScriptEditorContext";
import {
  buildEditModeText,
  isReactionSpan,
  labelOfReactionSpan,
  reconciledSpansFromEditText,
  resolveReactionFromLabel,
} from "Projects/Utils/spanUtils";
import { BlDropdown } from "Shared/Inputs/BlDropdown";
import { BlInput } from "Shared/Inputs/BlInput";
import type { BlOption } from "Shared/Inputs/BlOption";

interface ParametersTabContentProps {
  projectID: string;
}

const PauseParams = ({
  segment,
  index,
}: {
  segment: PauseSegmentFE;
  index: number;
}): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();
  const { updateSegment, isEditable } = useScriptEditor();

  const totalSeconds = segment.durationMs / 1000;

  const handleChange = (_: unknown, value: number | number[]) => {
    updateSegment(index, {
      ...segment,
      durationMs: Math.round((value as number) * 1000),
    });
  };

  return (
    <Stack spacing={Spacing.S}>
      <Typography variant="body2" fontWeight={600}>
        {t(Resources.JobDetail.PauseSegmentHeading)}
      </Typography>
      <Box>
        <Typography
          sx={{
            fontSize: "12px",
            color: theme.palette.text.secondary,
            mb: 0.5,
          }}
        >
          {t(Resources.JobDetail.PauseDuration)}: {totalSeconds.toFixed(1)}
          {t(Resources.JobDetail.Seconds)}
        </Typography>
        <Slider
          value={totalSeconds}
          min={0}
          max={30}
          step={0.5}
          disabled={!isEditable}
          onChange={handleChange}
          sx={{
            color: "info.main",
            "& .MuiSlider-thumb": { width: 14, height: 14 },
          }}
        />
      </Box>
    </Stack>
  );
};

const MediaParams = ({
  segment,
  index,
}: {
  segment: MediaSegmentFE;
  index: number;
}): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();
  const { updateSegment, isEditable } = useScriptEditor();
  const { data: mediaFiles } = useMediaFilesQuery({ limit: 50 });

  const musicOptions: BlOption[] = useMemo(() => {
    const noOption: BlOption = {
      value: "",
      label: t(Resources.JobDetail.NoMusic),
    };
    const fileOptions: BlOption[] =
      mediaFiles?.items.map((f) => ({
        value: f.partnerMediaFileID,
        label: f.displayName ?? f.fileName,
      })) ?? [];
    return [noOption, ...fileOptions];
  }, [mediaFiles, t]);

  const handleFileChange = (value: string | number | string[] | number[]) => {
    updateSegment(index, { ...segment, partnerMediaFileID: value as string });
  };

  const handleVolumeChange = (_: unknown, value: number | number[]) => {
    updateSegment(index, { ...segment, volumePercent: value as number });
  };

  const handleStartAtChange = (val: string) => {
    const seconds = parseFloat(val) || 0;
    updateSegment(index, {
      ...segment,
      startAtMs: Math.round(Math.max(0, seconds) * 1000),
    });
  };

  const handleDurationChange = (val: string) => {
    const parsed = parseFloat(val);
    const durationMs =
      val === "" || Number.isNaN(parsed) || parsed <= 0
        ? null
        : Math.round(parsed * 1000);
    updateSegment(index, { ...segment, durationMs });
  };

  return (
    <Stack spacing={Spacing.S}>
      <Typography variant="body2" fontWeight={600}>
        {t(Resources.JobDetail.BackgroundMusic)}
      </Typography>

      <BlDropdown
        name="mediaFile"
        placeholder={t(Resources.JobDetail.SelectBackgroundMusic)}
        options={musicOptions}
        value={segment.partnerMediaFileID}
        disabled={!isEditable}
        onChange={handleFileChange}
      />

      <Box>
        <Typography
          sx={{
            fontSize: "12px",
            color: theme.palette.text.secondary,
            mb: 0.5,
          }}
        >
          {t(Resources.JobDetail.MediaStartAt)}
        </Typography>
        <BlInput
          name="startAt"
          isNumber
          unit={t(Resources.JobDetail.Seconds)}
          maskProps={{
            scale: 1,
            min: 0,
            onMaskChange: handleStartAtChange,
          }}
          size="small"
          value={(segment.startAtMs / 1000).toFixed(1)}
          disabled={!isEditable}
        />
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: "12px",
            color: theme.palette.text.secondary,
            mb: 0.5,
          }}
        >
          {t(Resources.JobDetail.MediaDuration)}
        </Typography>
        <BlInput
          name="duration"
          isNumber
          unit={t(Resources.JobDetail.Seconds)}
          maskProps={{
            scale: 1,
            onMaskChange: handleDurationChange,
          }}
          size="small"
          placeholder="0"
          value={
            segment.durationMs != null
              ? (segment.durationMs / 1000).toFixed(1)
              : ""
          }
          disabled={!isEditable}
        />
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: "12px",
            color: theme.palette.text.secondary,
            mb: 0.5,
          }}
        >
          {t(Resources.JobDetail.VolumePercent)}:{" "}
          {Math.round(segment.volumePercent)}%
        </Typography>
        <Slider
          value={segment.volumePercent}
          min={0}
          max={100}
          disabled={!isEditable}
          onChange={handleVolumeChange}
          sx={{
            color: "success.main",
            "& .MuiSlider-thumb": { width: 14, height: 14 },
          }}
        />
      </Box>
    </Stack>
  );
};

/** Human-readable summary of the reaction on a reaction-span (one word). */
const reactionSummary = (
  span: VoiceSpan,
  t: (r: unknown) => string,
): string => {
  if (span.customReaction) return span.customReaction;
  const pick =
    span.reaction ?? span.reactionVolumeEnergy ?? span.reactionPacingRhythm;
  if (!pick) return "—";
  return t(
    Resources.JobDetail.Reaction.Values[
      pick as keyof typeof Resources.JobDetail.Reaction.Values
    ],
  );
};

const VoiceParams = ({
  segment,
  index,
  projectID,
}: {
  segment: VoiceSegmentFE;
  index: number;
  projectID: string;
}): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();

  const {
    updateSegment,
    removeReactionSpan,
    openReactionEditor,
    isSingleVoiceSpot,
    isEditable,
  } = useScriptEditor();

  const { data: actorVoices } = useProjectActorVoicesQuery(projectID);

  const actorVoiceOptions: BlOption[] = useMemo(() => {
    const empty: BlOption = {
      value: "",
      label: t(Resources.JobDetail.SelectVoiceModel),
    };
    const options: BlOption[] =
      actorVoices?.map((av) => ({
        value: av.actorVoiceID,
        label: `${av.actorName} – ${av.voiceName}`,
        disabled:
          av.actorStatus === ActorStatus.Inactive ||
          av.voiceStatus !== ActorVoiceStatus.Active,
      })) ?? [];
    return [empty, ...options];
  }, [actorVoices, t]);

  const handleVoiceChange = (value: string | number | string[] | number[]) => {
    updateSegment(index, { ...segment, actorVoiceID: value as string });
  };

  const handleLanguageChange = (
    value: string | number | string[] | number[],
  ) => {
    updateSegment(index, {
      ...segment,
      languageOverride: ((value as string) || null) as LanguageCodes | null,
    });
  };

  const handleSpanEmotionChange = (
    spanIdx: number,
    value: string | number | string[] | number[],
  ) => {
    const style = ((value as string) || null) as VoiceStyle | null;
    const newSpans = segment.spans.map((s, i) =>
      i === spanIdx ? { ...s, style } : s,
    );
    updateSegment(index, { ...segment, spans: newSpans });
  };

  const handleSpeedChange = (_: unknown, value: number | number[]) => {
    updateSegment(index, { ...segment, speedFactor: value as number });
  };

  const handleVolumeChange = (_: unknown, value: number | number[]) => {
    updateSegment(index, { ...segment, volumePercent: value as number });
  };

  const voice = actorVoices?.find(
    (av) => av.actorVoiceID === segment.actorVoiceID,
  );
  const isV3Voice = voice?.voiceLlm === VoiceLlm.ElevenLabs_V3;

  // Single-language providers (e.g. AIVoice) only support the voice's own catalog language, so the segment
  // language picker is restricted to it; multilingual providers (ElevenLabs / Microsoft) keep all languages.
  // Extension point: when a provider exposes several languages per voice, swap `=== voice.language` for a
  // membership check against the voice's supported-languages list.
  const isSingleLanguageVoice =
    voice != null && !isMultilingualVoiceLlm(voice.voiceLlm);
  const segmentLanguageOptions = isSingleLanguageVoice
    ? getSegmentLanguageOptions().filter((o) => o.value === voice?.language)
    : getSegmentLanguageOptions();

  const textSpanCount = segment.spans.filter((s) => !isReactionSpan(s)).length;
  const hasReactions = segment.spans.some((s) => isReactionSpan(s));
  const listingMode = textSpanCount > 1 || hasReactions;

  return (
    <Stack spacing={Spacing.S}>
      <Typography variant="body2" fontWeight={600}>
        {t(Resources.JobDetail.VoiceSegmentHeading)}
      </Typography>

      <Box>
        <Typography
          sx={{
            fontSize: "12px",
            color: theme.palette.text.secondary,
            mb: 0.5,
          }}
        >
          {t(Resources.JobDetail.SegmentText)}
        </Typography>
        <OutlinedInput
          multiline
          minRows={3}
          fullWidth
          disabled={!isEditable}
          value={buildEditModeText(segment.spans, (s) =>
            labelOfReactionSpan(s, t),
          )}
          onChange={(e) => {
            updateSegment(index, {
              ...segment,
              spans: reconciledSpansFromEditText(
                segment.spans,
                e.target.value,
                (s) => labelOfReactionSpan(s, t),
                (label) => resolveReactionFromLabel(label, t),
              ),
            });
          }}
        />
      </Box>

      {!isSingleVoiceSpot && (
        <BlDropdown
          name="actorVoice"
          labelProps={{ label: t(Resources.JobDetail.SelectVoiceModel) }}
          options={actorVoiceOptions}
          value={segment.actorVoiceID}
          disabled={!isEditable}
          onChange={handleVoiceChange}
        />
      )}

      <BlDropdown
        name="language"
        labelProps={{ label: t(Resources.JobDetail.Language) }}
        options={segmentLanguageOptions}
        value={
          isSingleLanguageVoice
            ? (voice?.language ?? "")
            : (segment.languageOverride ?? "")
        }
        disabled={!isEditable || isSingleLanguageVoice}
        onChange={handleLanguageChange}
      />

      {/* Emotion + reactions: one row per span when there are multiple text
           spans or reactions; otherwise a single emotion dropdown. */}
      {listingMode ? (
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mb: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                color: theme.palette.text.secondary,
                flex: 1,
              }}
            >
              {t(Resources.JobDetail.EmotionAndReactions)}
            </Typography>
            {hasReactions && !isV3Voice && (
              <Tooltip title={t(Resources.JobDetail.ReactionOnlyV3Hint)}>
                <InfoOutlinedIcon
                  sx={{ fontSize: 16, color: theme.palette.warning.main }}
                />
              </Tooltip>
            )}
          </Box>
          <Stack spacing={Spacing.XS}>
            {segment.spans.map((span, spanIdx) => {
              if (isReactionSpan(span)) {
                return (
                  <Box
                    // biome-ignore lint/suspicious/noArrayIndexKey: static span order
                    key={spanIdx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1,
                      py: 0.75,
                      borderRadius: "6px",
                      border: `1px solid ${theme.colors.border}`,
                      backgroundColor: theme.colors.backgroundInput,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "11px",
                        color: theme.colors.reactionAccent,
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      ◆ {t(Resources.JobDetail.Reaction.Heading)}:
                    </Typography>
                    <Typography
                      sx={{
                        flex: 1,
                        fontSize: "13px",
                        color: theme.palette.text.primary,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {reactionSummary(
                        span,
                        t as unknown as (r: unknown) => string,
                      )}
                    </Typography>
                    {isEditable && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon sx={{ fontSize: 12 }} />}
                        onClick={() =>
                          openReactionEditor(index, spanIdx, false)
                        }
                        sx={{ fontSize: "11px", py: 0.25, flexShrink: 0 }}
                      >
                        {t(Resources.JobDetail.Reaction.EditReaction)}
                      </Button>
                    )}
                    {isEditable && (
                      <IconButton
                        size="small"
                        onClick={() => removeReactionSpan(index, spanIdx)}
                        aria-label={t(
                          Resources.JobDetail.Reaction.RemoveReaction,
                        )}
                        sx={{
                          color: "text.secondary",
                          "&:hover": { color: "error.main" },
                          flexShrink: 0,
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                  </Box>
                );
              }
              return (
                <Box
                  // biome-ignore lint/suspicious/noArrayIndexKey: static span order
                  key={spanIdx}
                  sx={{ display: "flex", gap: 1, alignItems: "center" }}
                >
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: theme.palette.text.secondary,
                      fontFamily: "monospace",
                      flexShrink: 0,
                      maxWidth: "90px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={span.text}
                  >
                    „{span.text.slice(0, 20)}
                    {span.text.length > 20 ? "…" : ""}"
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <BlDropdown
                      name={`emotion-${spanIdx}`}
                      options={getVoiceStyleOptions()}
                      value={span.style ?? ""}
                      disabled={!isEditable}
                      onChange={(v) => handleSpanEmotionChange(spanIdx, v)}
                    />
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>
      ) : (
        <BlDropdown
          name="emotion"
          labelProps={{ label: t(Resources.JobDetail.Emotion) }}
          options={getVoiceStyleOptions()}
          value={segment.spans[0]?.style ?? ""}
          disabled={!isEditable}
          onChange={(v) => handleSpanEmotionChange(0, v)}
        />
      )}

      <Box>
        <Typography
          sx={{
            fontSize: "12px",
            color: theme.palette.text.secondary,
            mb: 0.5,
          }}
        >
          {t(Resources.JobDetail.SpeedFactor)}: ×
          {segment.speedFactor.toFixed(2)}
        </Typography>
        <Slider
          value={segment.speedFactor}
          min={0.7}
          max={1.2}
          step={0.05}
          disabled={!isEditable}
          onChange={handleSpeedChange}
          sx={{
            color: "primary.main",
            "& .MuiSlider-thumb": { width: 14, height: 14 },
          }}
        />
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: "12px",
            color: theme.palette.text.secondary,
            mb: 0.5,
          }}
        >
          {t(Resources.JobDetail.VolumePercent)}:{" "}
          {Math.round(segment.volumePercent)}%
        </Typography>
        <Slider
          value={segment.volumePercent}
          min={0}
          max={100}
          disabled={!isEditable}
          onChange={handleVolumeChange}
          sx={{
            color: "primary.main",
            "& .MuiSlider-thumb": { width: 14, height: 14 },
          }}
        />
      </Box>
    </Stack>
  );
};

export const ParametersTabContent = ({
  projectID,
}: ParametersTabContentProps): React.ReactNode => {
  const { t } = useResource();
  const { selectedSegmentIndex, script } = useScriptEditor();

  if (selectedSegmentIndex === null) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 4,
          color: "text.disabled",
          fontSize: "13px",
        }}
      >
        {t(Resources.JobDetail.NoSegmentSelected)}
      </Box>
    );
  }

  const segment = script.segments[selectedSegmentIndex];
  if (!segment) return null;

  if (isVoiceSegment(segment)) {
    return (
      <VoiceParams
        segment={segment}
        index={selectedSegmentIndex}
        projectID={projectID}
      />
    );
  }

  if (isPauseSegment(segment)) {
    return <PauseParams segment={segment} index={selectedSegmentIndex} />;
  }

  if (isMediaSegment(segment)) {
    return <MediaParams segment={segment} index={selectedSegmentIndex} />;
  }

  return null;
};
